import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Loader2, Clock, ExternalLink, AlertTriangle } from 'lucide-react';
import { API } from '../config/apiBase';


function getDeviceType() {
    const w = window.innerWidth;
    if (w < 768) return 'mobile';
    if (w < 1024) return 'tablet';
    return 'desktop';
}

function getBrowser() {
    const ua = navigator.userAgent;
    if (ua.includes('Chrome') && !ua.includes('Edg')) return 'Chrome';
    if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Edg')) return 'Edge';
    return 'Other';
}

function getOS() {
    const ua = navigator.userAgent;
    if (ua.includes('Windows')) return 'Windows';
    if (ua.includes('Mac')) return 'macOS';
    if (ua.includes('Android')) return 'Android';
    if (ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
    if (ua.includes('Linux')) return 'Linux';
    return 'Other';
}

function RedirectLandingPage() {
    const { slug } = useParams();
    const [searchParams] = useSearchParams();
    const [campaign, setCampaign] = useState(null);
    const [error, setError] = useState(null);
    const [countdown, setCountdown] = useState(null);
    const [paused, setPaused] = useState(false);
    const [redirected, setRedirected] = useState(false);
    const intervalRef = useRef(null);
    const sessionRef = useRef(`rs_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`);
    const loadTimeRef = useRef(Date.now());
    const trackedRef = useRef(new Set());

    const utms = {
        utm_source: searchParams.get('utm_source') || '',
        utm_medium: searchParams.get('utm_medium') || '',
        utm_campaign: searchParams.get('utm_campaign') || '',
        utm_content: searchParams.get('utm_content') || '',
        utm_term: searchParams.get('utm_term') || '',
    };

    const trackEvent = useCallback((eventName, extra = {}) => {
        if (!campaign) return;
        const dedupe_key = `${eventName}_${extra.button_type || ''}`;
        if (['page_view', 'page_loaded', 'countdown_started'].includes(eventName) && trackedRef.current.has(dedupe_key)) return;
        trackedRef.current.add(dedupe_key);
        const payload = {
            event: eventName,
            campaign_id: campaign.campaign_id || '',
            session_id: sessionRef.current,
            destination_url: campaign.destination_url || '',
            destination_type: campaign.destination_type || '',
            countdown_seconds: campaign.countdown_seconds || 0,
            time_on_page: Math.round((Date.now() - loadTimeRef.current) / 1000),
            time_to_action: Math.round((Date.now() - loadTimeRef.current) / 1000),
            ...utms,
            referer: document.referrer || '',
            device_type: getDeviceType(),
            browser: getBrowser(),
            os: getOS(),
            language: navigator.language || '',
            ...extra,
        };
        fetch(`${API}/r/${slug}/track`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }).catch(() => {});
    }, [campaign, slug, utms]);

    useEffect(() => {
        if (!slug) { setError('Link invalido'); return; }
        const isFirstVisit = !sessionStorage.getItem(`rc_visited_${slug}`);
        fetch(`${API}/r/${slug}`)
            .then(r => { if (!r.ok) throw new Error('not_found'); return r.json(); })
            .then(data => {
                setCampaign(data);
                setCountdown(data.countdown_seconds || 0);
                sessionStorage.setItem(`rc_visited_${slug}`, '1');
                // Track after setting campaign — use setTimeout to ensure state is set
                setTimeout(() => {
                    // We track directly here since campaign ref isn't ready in trackEvent yet
                    const base = {
                        campaign_id: data.campaign_id || '', session_id: sessionRef.current,
                        destination_url: data.destination_url || '', destination_type: data.destination_type || '',
                        countdown_seconds: data.countdown_seconds || 0, time_on_page: 0, time_to_action: 0,
                        ...Object.fromEntries(searchParams.entries()),
                        utm_source: searchParams.get('utm_source') || '', utm_medium: searchParams.get('utm_medium') || '',
                        utm_campaign: searchParams.get('utm_campaign') || '', utm_content: searchParams.get('utm_content') || '',
                        utm_term: searchParams.get('utm_term') || '',
                        referer: document.referrer || '', device_type: getDeviceType(), browser: getBrowser(), os: getOS(), language: navigator.language || '',
                    };
                    fetch(`${API}/r/${slug}/track`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ event: 'page_view', is_unique: isFirstVisit, ...base }) }).catch(() => {});
                    fetch(`${API}/r/${slug}/track`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ event: 'page_loaded', ...base }) }).catch(() => {});
                    if (data.countdown_seconds > 0) {
                        fetch(`${API}/r/${slug}/track`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ event: 'countdown_started', ...base }) }).catch(() => {});
                    }
                }, 100);
            })
            .catch(() => setError('not_found'));
    }, [slug, searchParams]);

    // Handle page abandon
    useEffect(() => {
        const handleUnload = () => {
            if (!redirected && campaign) {
                navigator.sendBeacon(`${API}/r/${slug}/track`, JSON.stringify({
                    event: 'abandoned', campaign_id: campaign.campaign_id || '', session_id: sessionRef.current,
                    time_on_page: Math.round((Date.now() - loadTimeRef.current) / 1000),
                    device_type: getDeviceType(), browser: getBrowser(), os: getOS(),
                }));
            }
        };
        window.addEventListener('beforeunload', handleUnload);
        return () => window.removeEventListener('beforeunload', handleUnload);
    }, [campaign, slug, redirected]);

    const doRedirect = useCallback((isAuto = false) => {
        if (redirected || !campaign) return;
        setRedirected(true);
        trackEvent(isAuto ? 'auto_redirect' : 'manual_redirect');
        if (campaign.countdown_seconds > 0) trackEvent('countdown_finished');
        let url = campaign.destination_url;
        if (campaign.preserve_query_params) {
            const qs = searchParams.toString();
            if (qs) url += (url.includes('?') ? '&' : '?') + qs;
        }
        setTimeout(() => { window.location.href = url; }, 200);
    }, [campaign, slug, searchParams, redirected, trackEvent]);

    // Countdown timer
    useEffect(() => {
        if (!campaign || countdown === null || paused || redirected) return;
        if (countdown <= 0 && campaign.auto_redirect) { doRedirect(true); return; }
        intervalRef.current = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) { clearInterval(intervalRef.current); return 0; }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(intervalRef.current);
    }, [campaign, countdown, paused, redirected, doRedirect]);

    useEffect(() => {
        if (countdown === 0 && campaign?.auto_redirect && !paused && !redirected) doRedirect(true);
    }, [countdown, campaign, paused, redirected, doRedirect]);

    const handlePrimary = () => {
        trackEvent('primary_click', { button_type: 'primary', button_text: campaign?.primary_button_text || '' });
        doRedirect(false);
    };

    const handleSecondary = () => {
        trackEvent('secondary_click', { button_type: 'secondary', button_text: campaign?.secondary_button_text || '' });
        const action = campaign?.secondary_button_action;
        if (action === 'cancel') { clearInterval(intervalRef.current); setPaused(true); setCountdown(null); trackEvent('cancelled'); }
        else if (action === 'pause') { setPaused(!paused); trackEvent('paused'); }
        else if (action === 'url' && campaign?.secondary_button_url) { window.location.href = campaign.secondary_button_url; }
    };

    if (error) return (
        <div style={{ minHeight: '100vh', background: '#071A3A', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Manrope', sans-serif" }}>
            <div style={{ textAlign: 'center', padding: 40 }}>
                <AlertTriangle size={48} color="#7DB3FF" style={{ margin: '0 auto 16px' }} />
                <h1 style={{ color: '#fff', fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Pagina indisponivel</h1>
                <p style={{ color: '#A8B6CC', fontSize: 14 }}>Esta campanha nao esta ativa ou o link e invalido.</p>
                <a href="https://www.supervitrineofertas.com" style={{ display: 'inline-block', marginTop: 24, padding: '10px 24px', background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)', borderRadius: 8, fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>Ir para SuperVitrine</a>
            </div>
        </div>
    );

    if (!campaign) return (
        <div style={{ minHeight: '100vh', background: '#071A3A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Loader2 size={32} color="#7DB3FF" className="animate-spin" />
        </div>
    );

    const accent = campaign.accent_color || '#7DB3FF';
    const btnColor = campaign.primary_button_color || '#3BEA86';
    const progress = campaign.countdown_seconds > 0 ? ((campaign.countdown_seconds - (countdown || 0)) / campaign.countdown_seconds) * 100 : 100;

    return (
        <div style={{ minHeight: '100vh', background: '#071A3A', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontFamily: "'Manrope', sans-serif", padding: '20px' }} data-testid="redirect-landing">
            {campaign.show_logo && <div style={{ marginBottom: 24 }}><span style={{ fontSize: 18, fontWeight: 800, color: accent, letterSpacing: -0.5 }}>SUPERVITRINE</span></div>}
            <div style={{ maxWidth: 480, width: '100%', background: '#0B214A', borderRadius: 16, border: `1px solid ${accent}22`, overflow: 'hidden' }}>
                {campaign.main_badge && <div style={{ padding: '8px 16px', background: `${accent}15`, textAlign: 'center' }}><span style={{ fontSize: 10, fontWeight: 700, color: accent, textTransform: 'uppercase', letterSpacing: 1.5 }}>{campaign.main_badge}</span></div>}
                <div style={{ padding: '28px 24px' }}>
                    {campaign.main_title && <h1 style={{ color: '#fff', fontSize: 20, fontWeight: 700, marginBottom: 8, lineHeight: 1.3 }}>{campaign.main_title}</h1>}
                    {campaign.main_message && <p style={{ color: '#A8B6CC', fontSize: 14, lineHeight: 1.6, marginBottom: 20 }}>{campaign.main_message}</p>}
                    {countdown !== null && countdown > 0 && (
                        <div style={{ marginBottom: 20 }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 8 }}>
                                <Clock size={14} color={accent} />
                                <span style={{ color: '#fff', fontSize: 24, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{countdown}</span>
                                <span style={{ color: '#6B7280', fontSize: 12 }}>segundo{countdown !== 1 ? 's' : ''}</span>
                            </div>
                            <div style={{ height: 3, background: '#1F1F1F', borderRadius: 4, overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: `${progress}%`, background: accent, borderRadius: 4, transition: 'width 1s linear' }} />
                            </div>
                        </div>
                    )}
                    {redirected && <p style={{ textAlign: 'center', color: accent, fontSize: 13, marginBottom: 16 }}>Redirecionando...</p>}
                    {paused && countdown === null && <p style={{ textAlign: 'center', color: '#6B7280', fontSize: 12, marginBottom: 16 }}>Redirecionamento cancelado.</p>}
                    {campaign.primary_button_enabled && !redirected && (
                        <button onClick={handlePrimary} data-testid="rc-primary-btn" style={{ width: '100%', padding: '12px', background: btnColor, color: '#071A3A', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: campaign.secondary_button_enabled ? 8 : 0 }}>
                            <ExternalLink size={14} /> {campaign.primary_button_text || 'Continuar'}
                        </button>
                    )}
                    {campaign.secondary_button_enabled && !redirected && (
                        <button onClick={handleSecondary} data-testid="rc-secondary-btn" style={{ width: '100%', padding: '10px', background: 'transparent', color: '#6B7280', border: '1px solid rgba(140,180,255,0.30)', borderRadius: 10, fontSize: 12, cursor: 'pointer' }}>
                            {campaign.secondary_button_text || 'Cancelar'}
                        </button>
                    )}
                </div>
                {campaign.ad_type !== 'none' && (campaign.ad_title || campaign.ad_text) && (
                    <div style={{ padding: '16px 24px', borderTop: '1px solid #1F1F1F', background: '#0D0D0D' }}>
                        {campaign.banner_tag && <span style={{ fontSize: 9, color: accent, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>{campaign.banner_tag}</span>}
                        {campaign.ad_title && <p style={{ color: '#E5E7EB', fontSize: 13, fontWeight: 600, marginTop: 4 }}>{campaign.ad_title}</p>}
                        {campaign.ad_text && <p style={{ color: '#6B7280', fontSize: 11, marginTop: 4, lineHeight: 1.5 }}>{campaign.ad_text}</p>}
                        {campaign.banner_title && <p style={{ color: accent, fontSize: 11, fontWeight: 700, marginTop: 8 }}>{campaign.banner_title}</p>}
                    </div>
                )}
            </div>
            <p style={{ marginTop: 16, fontSize: 10, color: '#4B5563' }}>SuperVitrine Ofertas</p>
        </div>
    );
}

export default RedirectLandingPage;
